import { ResponseError } from "lib/generated/integration-api";

/**
 * The body behind a failed push, when the service answered with the status asked for. The generated
 * client throws its own error type carrying the raw response, so the body is read from that.
 */
async function errorBody(
  error: unknown,
  status: number,
): Promise<Record<string, unknown> | undefined> {
  if (!(error instanceof ResponseError) || error.response.status !== status) return undefined;
  try {
    const body: unknown = await error.response.clone().json();
    return typeof body === "object" && body !== null
      ? (body as Record<string, unknown>)
      : undefined;
  } catch {
    return undefined;
  }
}

/**
 * The 403 for an object the connection was never granted. Thrown as itself so a caller can tell it
 * from a failure worth retrying with `instanceof`, rather than reading the body a second time.
 */
export class CrmPermissionRefused extends Error {
  constructor(readonly entityName: string) {
    super(`The CRM connection has not been given permission to read ${entityName}.`);
    this.name = "CrmPermissionRefused";
  }
}

/** Whether a failed request is that refusal, which only the response body says. */
export async function isPermissionRefused(error: unknown): Promise<boolean> {
  return (await errorBody(error, 403))?.code === "CRM_PERMISSION_MISSING";
}

/** The 422 the service answers with when required CRM fields are missing, naming them. */
export async function missingFieldsFrom(error: unknown): Promise<string[] | undefined> {
  const missing = (await errorBody(error, 422))?.missingFields;
  if (!Array.isArray(missing)) return undefined;
  const names = missing.filter((name): name is string => typeof name === "string");
  return names.length > 0 ? names : undefined;
}

/**
 * What the customer has to change to get a refused field accepted: `mapping` sends them to the
 * field mapping, `value` to the send form itself, `missing` means the CRM never received it at all.
 * Absent when the CRM did not say which.
 */
export type CrmFault = "mapping" | "value" | "missing";

/** One CRM field the provider refused, and why, from the service's 400. */
export type InvalidCrmField = { name: string; reason: string; fault?: CrmFault };

/**
 * The 400 the service answers when the CRM refuses the values themselves — nearly always a field
 * mapping pointed at a field of the wrong type. Naming the field is the whole value here: without
 * it the customer is told only that something went wrong, and the mapping is configured elsewhere.
 */
export async function invalidFieldsFrom(error: unknown): Promise<InvalidCrmField[] | undefined> {
  const invalid = (await errorBody(error, 400))?.invalidFields;
  if (!Array.isArray(invalid)) return undefined;
  const fields = invalid.flatMap((entry): InvalidCrmField[] => {
    if (typeof entry !== "object" || entry === null) return [];
    const { name, reason, fault } = entry as { name?: unknown; reason?: unknown; fault?: unknown };
    if (typeof name !== "string" || name.length === 0) return [];
    return [
      {
        name,
        reason: typeof reason === "string" ? reason : "",
        ...(fault === "mapping" || fault === "value" ? { fault } : {}),
      },
    ];
  });
  return fields.length > 0 ? fields : undefined;
}

/**
 * Every CRM field a refusal named, whichever of the two ways it named them. Both answers put the
 * same thing in front of the user — fields the push cannot go through as it stands — so a form
 * showing them does not care which one arrived.
 *
 * A required field the CRM never received carries no reason: there is nothing to say beyond its
 * name, which is why `reason` is empty rather than invented. It is marked `missing` all the same,
 * because that is the one refusal the form can answer by growing a control to collect it.
 */
export async function refusedFieldsFrom(error: unknown): Promise<InvalidCrmField[]> {
  const missing = await missingFieldsFrom(error);
  if (missing) return missing.map((name) => ({ name, reason: "", fault: "missing" as const }));
  return (await invalidFieldsFrom(error)) ?? [];
}
